/**
 * Grafana Faro bootstrap for the supplier portal SPA.
 *
 * Vendored copy of apps/customer-portal/src/observability/faro.ts — see
 * ../app/components/primitives/README.md for why the shells duplicate rather
 * than share their web-only plumbing. Captures unhandled errors, Web Vitals
 * and traces, with `traceparent` propagated only to the API base URL.
 *
 * Safe-by-construction: returns without starting unless `faroEnabled` and a
 * collector URL are configured; the caller wraps it in try/catch so an SDK
 * failure never stops the app booting. Event-naming conventions:
 * `.claude/rules/logging.md`.
 */

import { initializeFaro, getWebInstrumentations } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";

const APP_NAME = "customer-intelligence-supplier-portal";

export function initFaro(): void {
  // Read inside the function: configuration resolves during bootstrap and
  // this module sits in main.tsx's static import graph.
  const {
    faroEnabled,
    faroCollectorUrl: COLLECTOR_URL,
    appVersion: APP_VERSION,
    environment: ENVIRONMENT,
    apiBaseUrl: API_BASE_URL,
  } = getRuntimeConfig();

  if (!faroEnabled) return;
  if (!COLLECTOR_URL) {
    console.warn(
      "[faro] faroEnabled is set but the collector URL is empty; skipping init.",
    );
    return;
  }

  const propagateTargets: (string | RegExp)[] = API_BASE_URL
    ? [new RegExp(`^${escapeRegExp(API_BASE_URL)}`)]
    : [];

  initializeFaro({
    url: COLLECTOR_URL,
    app: {
      name: APP_NAME,
      version: APP_VERSION,
      environment: ENVIRONMENT,
    },
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: propagateTargets,
        },
      }),
    ],
  });
}

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
